import { useMemo, type JSX } from "react";
import type { ReviewStepCurrent } from "../../../data/chatQuery";
import { DraftWithDeviation, htmlToText } from "./reviewText";
import { wordDiff } from "./wordDiff";

// The clause with the change marked inline: removed words struck in red, inserted
// words highlighted green. Used by the card and by the Why dialog so both show the
// same marking. Falls back to the draft text (deviation struck) when there is no
// recommendation or a word diff would not read well.
export function ClauseDiffLine({ cur }: { cur: ReviewStepCurrent }): JSX.Element {
  const segs = useMemo(() => {
    if (cur.recommendation === null) return null;
    return wordDiff(htmlToText(cur.draftText), htmlToText(cur.recommendation.text));
  }, [cur.draftText, cur.recommendation]);
  // [review-remove] The proposal is to take the whole clause out: strike all of it.
  if (cur.recommendation?.action === "remove") {
    return <span className="sa-cr__del" data-testid="review-remove-line">{htmlToText(cur.draftText)}</span>;
  }
  // [review-add] A standard clause the draft lacks: all of it is new text.
  if (cur.recommendation?.action === "add") {
    return <span className="sa-cr__ins" data-testid="review-add-line">{htmlToText(cur.recommendation.text)}</span>;
  }
  if (segs === null) {
    // A whole-clause replacement would strike the entire paragraph red; show it
    // plain instead (the Why dialog carries the draft-vs-certified comparison).
    const plain = htmlToText(cur.draftText);
    const span = cur.deviationSpan !== null ? htmlToText(cur.deviationSpan) : "";
    const wholeClause = span.length > 0 && span.length >= plain.length * 0.8;
    return (
      <DraftWithDeviation text={cur.draftText} span={wholeClause ? null : cur.deviationSpan} />
    );
  }
  return (
    <>
      {segs.map((s, i) => (
        <span key={i}>
          {i > 0 ? " " : ""}
          {s.kind === "same" ? (
            s.text
          ) : (
            <span className={s.kind === "del" ? "sa-cr__del" : "sa-cr__ins"}>{s.text}</span>
          )}
        </span>
      ))}
    </>
  );
}
